<template>
  <v-container>
    <v-card>
      <v-card-title>All Orders</v-card-title>
      <v-card-text>
        <v-row class="mb-4" dense>
          <!-- FROM DATE -->
          <v-col cols="12" md="2">
            <v-menu v-model="fromMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
              <template #activator="{ props }">
                <v-text-field v-bind="props" :model-value="formattedFromDate" label="From Date" readonly dense clearable
                  @click:clear="clearFromDate" />
              </template>
              <v-date-picker v-model="fromDateRaw" :max="toDateRaw" color="primary"
                @update:model-value="fromMenu = false" />
            </v-menu>
          </v-col>
  
          <!-- TO DATE -->
          <v-col cols="12" md="2">
            <v-menu v-model="toMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
              <template #activator="{ props }">
                <v-text-field v-bind="props" :model-value="formattedToDate" label="To Date" readonly dense clearable
                  @click:clear="clearToDate" />
              </template>
              <v-date-picker v-model="toDateRaw" :min="fromDateRaw" color="primary"
                @update:model-value="toMenu = false" />
            </v-menu>
          </v-col>
  
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn color="primary" size="small" icon title="Fetch Orders" @click="fetchOrdersByDate">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
  
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <span class="text-black font-weight-bold">Total Orders: {{ totalOrders }}</span>
        <v-row class="mb-4" dense />
  
        <v-data-table :headers="headers" :items="orders" :items-per-page="itemsPerPage" :page="page"
          :server-items-length="totalOrders" :loading="loading" :footer-props="{
                  'items-per-page-options': [10, 25, 50, 100, -1],
                  'items-per-page-text': 'Items per page:',
                  'items-per-page-all-text': 'All'
                }" @update:items-per-page="onItemsPerPageChange" @update:page="onPageChange">
          <template #[`item.expiry_date`]="{ item }">
            {{ formatDate(item?.expiry_date ?? '') }}
          </template>
          <template #[`item.order_datetime`]="{ item }">
            {{ formatDate(item?.order_datetime ?? '') }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn v-if="!item.isPartOfPosition" color="secondary" class="rounded-pill text-caption" size="small"
              @click="moveOrderToPosition(item.order_id)">
              To Position
            </v-btn>
          </template>
          <template #[`item.application`]="{ item }">
            <div class="d-flex align-center">
              <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'" :alt="item.application"
                :max-width="item.application === 'BREEZE' ? 50 : 25" :max-height="item.application === 'BREEZE' ? 50 : 25"
                cover />
            </div>
          </template>
        </v-data-table>
  
        <v-divider class="my-4" />
  
        <!-- Pagination Controls -->
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="6">
            <v-btn color="primary" class="mr-2" :disabled="page <= 1 || itemsPerPage === totalOrders" @click="prevPage">
              Previous
            </v-btn>
            <v-btn color="primary" :disabled="page >= totalPages || itemsPerPage === totalOrders" @click="nextPage">
              Next
            </v-btn>
          </v-col>
  
          <v-col cols="12" md="6" class="d-flex justify-end">
            <span class="font-weight-medium">Page {{ page }} of {{ totalPages || 1 }}</span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor" location="top right">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      fromDateRaw: null,
      toDateRaw: null,
      fromMenu: false,
      toMenu: false,
      loading: true,
      page: 1,
      itemsPerPage: 10,
      totalOrders: 0,
      orders: [],
      selectedOrders: [],
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      headers: [
        { title: 'Order ID', value: 'exchange_order_id' },
        { title: 'Application', value: 'application' },
        { title: 'Stock', value: 'stock_code' },
        { title: 'Action', value: 'action' },
        { title: 'Qty', value: 'quantity' },
        { title: 'Avg Price', value: 'average_price' },
        { title: 'LTP', value: 'ltp' },
        { title: 'Expiry', value: 'expiry_date' },
        { title: 'Strike', value: 'strike_price' },
        { title: 'Right', value: 'right' },
        { title: 'Execution', value: 'order_datetime', sortable: true },
        { title: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalOrders / this.itemsPerPage)
    },
    formattedFromDate() {
      if (!this.fromDateRaw) return ''
      const d = new Date(this.fromDateRaw)
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    },
    formattedToDate() {
      if (!this.toDateRaw) return ''
      const d = new Date(this.toDateRaw)
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    }
  },
  mounted() {
    this.fetchOrders()
  },
  methods: {
    clearFromDate() {
      this.fromDateRaw = null
    },
    clearToDate() {
      this.toDateRaw = null
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const day = date.getDate().toString().padStart(2, '0')
      const month = date.toLocaleString('en-US', { month: 'short' })
      const year = date.getFullYear().toString().slice(-2)
      return `${day}-${month}-${year}`
    },
    async moveOrderToPosition(orderId) {
      try {
        this.loading = true
        await this.$store.dispatch('orders/moveOrderToPosition', { order_id: orderId })
        this.snackbarMessage = 'Order moved successfully'
        this.snackbarColor = 'success'
        this.snackbar = true
        this.fetchOrders()
      } catch (error) {
        this.snackbarMessage = 'Failed to move order'
        this.snackbarColor = 'error'
        this.snackbar = true
      } finally {
        this.loading = false
      }
    },
    async fetchOrdersByDate() {
      if (!this.fromDateRaw || !this.toDateRaw) {
        alert('Please select both From and To dates')
        return
      }

      this.loading = true
      try {
        await this.$store.dispatch('orders/fetchOrdersByDate', {
          from: this.fromDateRaw,
          to: this.toDateRaw
        })
        this.page = 1
        await this.fetchOrders()
      } catch (error) {
        console.error('Error fetching by date:', error)
      } finally {
        this.loading = false
      }
    },
    async fetchOrders() {
      this.loading = true
      try {
        const res = await this.$store.dispatch('orders/fetchOrders', {
          offset: (this.page - 1) * this.itemsPerPage,
          count: this.itemsPerPage
        })
        this.orders = res.orderDisplayList || []
        this.totalOrders = res.totalCount || 0
      } catch (err) {
        console.error('Failed to fetch orders:', err)
      } finally {
        this.loading = false
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchOrders()
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--
        this.fetchOrders()
      }
    },
    onPageChange(newPage) {
      this.page = newPage
      this.fetchOrders()
    },
    onItemsPerPageChange(value) {
      this.page = 1
      this.itemsPerPage = value === -1 ? this.totalOrders : value
      this.fetchOrders()
    }
  }
}
</script>

<style scoped>
:deep(th) {
  font-weight: bold !important;
  font-size: 14px !important;
  background-color: #fbeee6 !important;
}
</style>
